import React, { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";

// We define the interface for props passed to the configuration panel.
// This allows the parent component (ConfiguratorPage) to feed layoutId and layout settings.
export interface EdgeConfigPanelProps {
  layoutId: Id<"screenLayouts">;
  edgeConfig: {
    mode: "instant" | "modifier" | "dwell";
    modifierKey?: string;
    dwellMs?: number;
  };
}

/**
 * EdgeConfigPanel Component
 * 
 * This panel allows users to configure how the mouse cursor behaves when crossing screen boundaries.
 * It provides three main modes:
 * 1. Instant: The mouse crosses immediately upon touching the screen edge.
 * 2. Modifier Key: The mouse only crosses if a specific modifier key (like Ctrl, Alt, or Shift) is held down.
 * 3. Dwell Time: The mouse must rest on the edge for a specified duration (in milliseconds) before crossing.
 * 
 * Non-coder guide:
 * - To add new modifier keys, update the OPTIONS in the modifierKey select list.
 * - To change default dwell durations, adjust the default state values.
 * - You can tweak styling by modifying the tailwind-like custom CSS classes in src/styles/configurator.css.
 */
export const EdgeConfigPanel: React.FC<EdgeConfigPanelProps> = ({ layoutId, edgeConfig }) => {
  // Setup the Convex mutation hook to persist edge configuration settings back to the database.
  const updateEdgeConfig = useMutation(api.layouts.updateEdgeConfig);

  // Local state to manage form values before they are committed to the database.
  const [mode, setMode] = useState<"instant" | "modifier" | "dwell">(edgeConfig?.mode || "instant");
  const [modifierKey, setModifierKey] = useState<string>(edgeConfig?.modifierKey || "Ctrl");
  const [dwellMs, setDwellMs] = useState<number>(edgeConfig?.dwellMs || 250);

  // Status states for tracking mutation execution progress
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Keep local state in sync if the backend layout configuration updates from another component/device
  useEffect(() => {
    if (edgeConfig) {
      setMode(edgeConfig.mode);
      if (edgeConfig.modifierKey) setModifierKey(edgeConfig.modifierKey);
      if (edgeConfig.dwellMs) setDwellMs(edgeConfig.dwellMs);
    }
  }, [edgeConfig]);

  // Handler triggered when the user clicks "Save Settings"
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Build the configuration payload based on the selected mode.
      // We clean up irrelevant options (e.g. removing dwellMs if mode is modifier) to avoid database bloat.
      const payload: {
        mode: "instant" | "modifier" | "dwell";
        modifierKey?: string;
        dwellMs?: number;
      } = {
        mode,
      };
      if (mode === "modifier") {
        payload.modifierKey = modifierKey;
      } else if (mode === "dwell") {
        // Verbose comments for non-coders:
        // We validate that the user's input for dwellMs delay is within the acceptable boundaries.
        // It must be a valid number, and it must be between 50 and 2000 milliseconds inclusive.
        // To tweak settings, change the 50 (min) or 2000 (max) limits as required by product rules.
        const val = Number(dwellMs);
        if (isNaN(val) || val < 50 || val > 2000) {
          setError("Dwell delay must be between 50 and 2000 milliseconds.");
          setIsSaving(false);
          return;
        }
        payload.dwellMs = val;
      }

      // Execute mutation on the Convex backend.
      // Note: The backend schema expects a nested 'edgeConfig' object: { layoutId, edgeConfig: { mode, ... } }
      await updateEdgeConfig({
        layoutId,
        edgeConfig: payload,
      });

      setSuccess(true);
      // Auto-hide the success banner after 3 seconds to keep UI clean
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      console.error("Failed to save edge configuration:", err);
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred while saving your settings.";
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className="edge-config-panel"
      data-testid="edge-config-panel"
      style={{
        padding: "1.5rem",
        backgroundColor: "var(--color-bg-secondary)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--border-radius-lg)",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        maxWidth: "400px",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem"
      }}
    >
      <div>
        <h3 style={{ fontSize: "1.25rem", fontWeight: 700, margin: 0, color: "var(--color-text-primary)" }}>
          Edge Crossing Behavior
        </h3>
        <p style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)", marginTop: "0.25rem" }}>
          Configure how the cursor crosses between your screens.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* Mode Selector */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <label 
            htmlFor="edge-mode-select" 
            style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text-primary)" }}
          >
            Trigger Mode
          </label>
          <select
            id="edge-mode-select"
            data-testid="mode-select"
            value={mode}
            onChange={(e) => setMode(e.target.value as "instant" | "modifier" | "dwell")}
            disabled={isSaving}
            style={{
              padding: "0.5rem",
              backgroundColor: "var(--color-bg-primary)",
              color: "var(--color-text-primary)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--border-radius-md)",
              fontSize: "0.9rem",
              cursor: isSaving ? "not-allowed" : "pointer"
            }}
          >
            <option value="instant">Instant (Default)</option>
            <option value="modifier">Modifier Key</option>
            <option value="dwell">Dwell Time</option>
          </select>
        </div>

        {/* Conditional Rendering: Modifier Key Settings */}
        {mode === "modifier" && (
          <div 
            data-testid="modifier-config"
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <label 
              htmlFor="modifier-key-select" 
              style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text-primary)" }}
            >
              Required Activation Key
            </label>
            <p style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", margin: 0 }}>
              Hold this key down while moving your mouse to cross boundaries.
            </p>
            <select
              id="modifier-key-select"
              data-testid="modifier-key-select"
              value={modifierKey}
              onChange={(e) => setModifierKey(e.target.value)}
              disabled={isSaving}
              style={{
                padding: "0.5rem",
                backgroundColor: "var(--color-bg-primary)",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--border-radius-md)",
                fontSize: "0.9rem",
                cursor: isSaving ? "not-allowed" : "pointer"
              }}
            >
              <option value="Ctrl">Control (Ctrl)</option>
              <option value="Shift">Shift</option>
              <option value="Alt">Alt / Option</option>
            </select>
          </div>
        )}

        {/* Conditional Rendering: Dwell Time Settings */}
        {mode === "dwell" && (
          <div 
            data-testid="dwell-config"
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <label 
              htmlFor="dwell-ms-input" 
              style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text-primary)" }}
            >
              Dwell Delay (milliseconds)
            </label>
            <p style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", margin: 0 }}>
              Amount of time the cursor must rest on the edge before crossing.
            </p>
            <input
              id="dwell-ms-input"
              data-testid="dwell-ms-input"
              type="number"
              min="50"
              max="2000"
              step="50"
              value={dwellMs}
              onChange={(e) => setDwellMs(Number(e.target.value))}
              disabled={isSaving}
              style={{
                padding: "0.5rem",
                backgroundColor: "var(--color-bg-primary)",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--border-radius-md)",
                fontSize: "0.9rem",
                cursor: isSaving ? "not-allowed" : "text"
              }}
            />
          </div>
        )}

        {/* Error message block */}
        {error && (
          <div 
            data-testid="error-message"
            style={{
              padding: "0.5rem 0.75rem",
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              border: "1px solid var(--color-error)",
              borderRadius: "var(--border-radius-md)",
              color: "var(--color-error)",
              fontSize: "0.8rem"
            }}
          >
            {error}
          </div>
        )}

        {/* Success message banner */}
        {success && (
          <div 
            data-testid="success-message"
            style={{
              padding: "0.5rem 0.75rem",
              backgroundColor: "rgba(16, 185, 129, 0.1)",
              border: "1px solid var(--color-accent)",
              borderRadius: "var(--border-radius-md)",
              color: "var(--color-accent)",
              fontSize: "0.8rem"
            }}
          >
            Settings saved successfully!
          </div>
        )}

        {/* Action Button */}
        <button
          type="submit"
          data-testid="save-btn"
          disabled={isSaving}
          style={{
            padding: "0.6rem",
            backgroundColor: "var(--color-accent)",
            color: "var(--color-bg-primary)",
            fontWeight: 700,
            border: "none",
            borderRadius: "var(--border-radius-md)",
            cursor: isSaving ? "not-allowed" : "pointer",
            transition: "background-color 0.2s",
            fontSize: "0.9rem",
            opacity: isSaving ? 0.7 : 1
          }}
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </button>
      </form>
    </div>
  );
};
